import React from 'react'
import { PlusCircle } from 'lucide-react'

import LoadingIndicator from 'components/LoadingIndicator'
import { DeliveryReportHeader } from 'components/DeliveryReportHeader'
import { SpSelect } from 'components/SpSelect'
import type { ComboBoxOption, ComboBoxChange } from 'components/ComboBox'
import { SpConfirmModal } from 'components/SpConfirmModal'
import type { SnackbarPayload } from 'components/Snackbar/useSnackbar'

import type { DeliveryWorkTypeSetting } from 'repositories/ShipmentApi/getDeliveryWorkTypeSettings/type'
import { ShipmentVisitBlock } from './ShipmentVisitBlock'
import type { Visit } from '../utils/deliveryWorkMappers'
import styles from './DeliveryWorkView.module.css'

type Option = {
  label: string
  value: string | number
}

type Props = {
  tenantName: string
  deliveryReportUid: string
  shipmentUid: string
  driverPhone: string
  notify: (payload: SnackbarPayload) => void
  onDeliveryWorkSaved: (savedVisitId: number) => Promise<void>
  onVisitDirtyChange: (visitId: string, dirty: boolean) => void
  pageState: 'no_shipment' | 'has_shipment'
  isCreating: boolean
  pickBookingId: string | null
  pickBookingOptions: Option[]
  visits: Visit[]
  workTypeSettings: DeliveryWorkTypeSetting[]
  deliveryDestinationOptions: ComboBoxOption[]
  deliveryHistoryValues: ReadonlySet<string>
  onStartDelivery: () => void
  onNewDeliveryFromMenu: () => void
  onPickBookingChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  onDeliveryTenantChange: (visitId: string, next: ComboBoxChange) => void
  onSelectDelivery: (visitId: string) => void
  onClearDelivery: (visitId: string) => void
  onAddVisit: () => void
  isStamping: boolean
  isMutatingVisit: boolean
  onStampClick: (visitId: string, type: 'checkin' | 'start' | 'end' | 'checkout') => void
  onDeleteVisit: (visitId: string) => void
  onMoveVisitUp: (visitId: string) => void
  onMoveVisitDown: (visitId: string) => void
  isCandidatesLoading: boolean
  isPickBookingUpdating: boolean
  isDeliveryCompleted: boolean
  isDeliveryProcessing: boolean
  confirmType: 'complete' | 'cancel' | null
  onCompleteDelivery: () => void
  onCancelDeliveryComplete: () => void
  onDeliveryConfirmOk: () => void
  onDeliveryConfirmClose: () => void
}

export const DeliveryWorkView: React.FC<Props> = ({
  tenantName,
  deliveryReportUid,
  shipmentUid,
  driverPhone,
  notify,
  onDeliveryWorkSaved,
  onVisitDirtyChange,
  pageState,
  isCreating,
  pickBookingId,
  pickBookingOptions,
  visits,
  workTypeSettings,
  deliveryDestinationOptions,
  deliveryHistoryValues,
  isStamping,
  onStartDelivery,
  onNewDeliveryFromMenu,
  onPickBookingChange,
  onDeliveryTenantChange,
  onSelectDelivery,
  onClearDelivery,
  onAddVisit,
  isMutatingVisit,
  onStampClick,
  onDeleteVisit,
  onMoveVisitUp,
  onMoveVisitDown,
  isCandidatesLoading,
  isPickBookingUpdating,
  isDeliveryCompleted,
  isDeliveryProcessing,
  confirmType,
  onCompleteDelivery,
  onCancelDeliveryComplete,
  onDeliveryConfirmOk,
  onDeliveryConfirmClose,
}) => (
  <>
    <div className={styles.page}>
      {/* ヘッダー〜メインコンテンツ。配送完了ボタン領域は下端固定にするため別要素に分離する */}
      <div className={styles.pageMain}>
        <DeliveryReportHeader
          title="納品報告"
          deliveryReportUid={deliveryReportUid}
          onNewDelivery={onNewDeliveryFromMenu}
        />

        {/* 出荷元セクション */}
        <div className={styles.tenantLabelRow}>
          <p className={styles.tenantLabel}>出荷元</p>
        </div>
        <div className={styles.tenantNameRow}>
          <p className={styles.tenantName}>{tenantName}</p>
          {pageState === 'has_shipment' &&
            !isCandidatesLoading &&
            pickBookingOptions.length > 0 && (
              <SpSelect
                options={pickBookingOptions}
                value={pickBookingId}
                placeholder="任意：予約・受付を選択できます"
                onChange={onPickBookingChange}
                className={styles.pickBookingSelect}
                isDisabled={isDeliveryCompleted || isPickBookingUpdating}
              />
            )}
        </div>

        {pageState === 'has_shipment' && (
          <>
            {/* 納品先セクション */}
            <div className={styles.tenantLabelRow}>
              <p className={styles.tenantLabel}>納品先</p>
            </div>
            <div className={styles.visitsArea}>
              {visits.map((visit, index) => (
                <ShipmentVisitBlock
                  key={visit.id}
                  visitId={visit.id}
                  visitNumber={index + 1}
                  shipmentUid={shipmentUid}
                  dbVisitId={visit.dbVisitId}
                  deliveryReportUid={deliveryReportUid}
                  driverPhone={driverPhone}
                  deliveryWork={visit.deliveryWork}
                  deliveryWorkAttachments={visit.deliveryWorkAttachments}
                  workTypeSettings={workTypeSettings}
                  notify={notify}
                  onSaved={onDeliveryWorkSaved}
                  onDirtyChange={onVisitDirtyChange}
                  deliveryDestinationOptions={deliveryDestinationOptions}
                  deliveryHistoryValues={deliveryHistoryValues}
                  deliveryTenantLabel={visit.deliveryTenantLabel}
                  savedDeliveryLabel={visit.savedDeliveryLabel}
                  savedDeliverySource={visit.savedDeliverySource}
                  stamps={visit.stamps}
                  isCompleted={isDeliveryCompleted}
                  showControls={visits.length >= 2 && !isDeliveryCompleted}
                  canMoveUp={index > 0}
                  canMoveDown={index < visits.length - 1}
                  isMutatingVisit={isMutatingVisit}
                  onDeliveryTenantChange={(next) => onDeliveryTenantChange(visit.id, next)}
                  onSelectDelivery={() => onSelectDelivery(visit.id)}
                  onClearDelivery={() => onClearDelivery(visit.id)}
                  isStamping={isStamping}
                  onStampClick={(type) => onStampClick(visit.id, type)}
                  onDelete={() => onDeleteVisit(visit.id)}
                  onMoveUp={() => onMoveVisitUp(visit.id)}
                  onMoveDown={() => onMoveVisitDown(visit.id)}
                />
              ))}

              {!isDeliveryCompleted && (
                <button
                  className={styles.addVisitButton}
                  onClick={onAddVisit}
                  type="button"
                  disabled={isMutatingVisit || isStamping}
                >
                  <PlusCircle size={32} className={styles.addVisitIcon} aria-hidden={true} />
                  <span className={styles.addVisitText}>次の納品先を追加</span>
                </button>
              )}
            </div>
          </>
        )}

        {pageState === 'no_shipment' && (
          <div className={styles.body}>
            <button className={styles.startButton} onClick={onStartDelivery} disabled={isCreating}>
              新規配送開始
            </button>
          </div>
        )}
      </div>

      {/* 配送完了ボタン領域: 画面下端に固定表示する */}
      {pageState === 'has_shipment' && (
        <div className={styles.completeDeliveryRow}>
          {isDeliveryCompleted ? (
            <button
              className={styles.cancelDeliveryButton}
              onClick={onCancelDeliveryComplete}
              disabled={isDeliveryProcessing}
            >
              完了取消
            </button>
          ) : (
            <>
              <p className={styles.completeDeliveryGuide}>
                <span className={styles.completeDeliveryGuideLine1}>すべての作業が完了後</span>
                <span className={styles.completeDeliveryGuideLine2}>
                  「配送完了」ボタンを押して下さい
                </span>
              </p>
              <button
                className={styles.completeDeliveryButton}
                onClick={onCompleteDelivery}
                disabled={isDeliveryProcessing}
              >
                配送完了
              </button>
            </>
          )}
        </div>
      )}
    </div>

    {isDeliveryProcessing && <LoadingIndicator />}
    <SpConfirmModal
      isOpen={confirmType === 'complete'}
      title="配送を完了しますか？"
      description="全ての納品が完了しましたか？"
      variant="primary"
      bordered
      onOk={onDeliveryConfirmOk}
      onCancel={onDeliveryConfirmClose}
    />
    <SpConfirmModal
      isOpen={confirmType === 'cancel'}
      title="配送完了を取消しますか？"
      variant="cancel"
      onOk={onDeliveryConfirmOk}
      onCancel={onDeliveryConfirmClose}
    />
  </>
)
